// 應用配置
export interface AppConfig {
  // API 配置
  api: {
    baseURL: string
    timeout: number
    retryAttempts: number
    retryDelay: number
  }
  
  // 認證配置
  auth: {
    accessTokenKey: string    // accessToken的存儲鍵名
    refreshTokenKey: string   // refreshToken的存儲鍵名
    userInfoKey: string       // 用戶信息的存儲鍵名
    sessionTimeout: number    // 會話超時時間（毫秒）
  }
  
  // 遊戲配置
  game: {
    countdownDuration: number // 遊戲倒計時（秒）
    refreshInterval: number // 期數刷新間隔（毫秒）
    defaultBetAmount: number // 默認投注金額
    maxBetAmount: number // 最大投注金額
  }
  
  // UI 配置
  ui: {
    loadingTimeout: number // loading 超時時間（毫秒）
    toastDuration: number // 提示消息顯示時間（毫秒）
    animationDuration: number // 動畫持續時間（毫秒）
  }
  
  // 開發配置
  dev: {
    mockApi: boolean // 是否使用模擬 API
    enableLogging: boolean // 是否啟用日誌
    debugMode: boolean // 調試模式
  }
}

// 環境配置
const environments = {
  development: {
    api: {
      baseURL: import.meta.env.DEV ? '/api' : (import.meta.env.VITE_API_URL || 'https://service-dev.eazy-pe.com'), // DEV為true時使用代理，構建時使用完整URL
      timeout: 10000,
      retryAttempts: 3,
      retryDelay: 1000
    },
    auth: {
      accessTokenKey: 'accessToken',
      refreshTokenKey: 'refreshToken', 
      userInfoKey: 'userInfo',
      sessionTimeout: 24 * 60 * 60 * 1000 // 24小時
    },
    game: {
      countdownDuration: 120,
      refreshInterval: 30000,
      defaultBetAmount: 10,
      maxBetAmount: 50000
    },
    ui: {
      loadingTimeout: 10000,
      toastDuration: 2000,
      animationDuration: 300
    },
    dev: {
      mockApi: false,
      enableLogging: false,
      debugMode: false
    }
  },
  
  production: {
    api: {
      baseURL: import.meta.env.VITE_API_URL || '/api',
      timeout: 15000,
      retryAttempts: 2,
      retryDelay: 1500
    },
    auth: {
      accessTokenKey: 'accessToken',
      refreshTokenKey: 'refreshToken',
      userInfoKey: 'userInfo',
      sessionTimeout: 8 * 60 * 60 * 1000 // 8小時
    },
    game: {
      countdownDuration: 120,
      refreshInterval: 10000,
      defaultBetAmount: 10,
      maxBetAmount: 50000
    },
    ui: {
      loadingTimeout: 8000,
      toastDuration: 3000,
      animationDuration: 300
    },
    dev: {
      mockApi: false,
      enableLogging: false,
      debugMode: false
    }
  },
  
  test: {
    api: {
      baseURL: 'http://localhost:3001/api',
      timeout: 5000,
      retryAttempts: 1,
      retryDelay: 500
    },
    auth: {
      accessTokenKey: 'test_accessToken',
      refreshTokenKey: 'test_refreshToken',
      userInfoKey: 'test_userInfo',
      sessionTimeout: 30 * 60 * 1000 // 30分鐘
    },
    game: {
      countdownDuration: 30,
      refreshInterval: 5000,
      defaultBetAmount: 1,
      maxBetAmount: 1000
    },
    ui: {
      loadingTimeout: 3000,
      toastDuration: 1000,
      animationDuration: 100
    },
    dev: {
      mockApi: true,
      enableLogging: true,
      debugMode: true
    }
  }
}

// 獲取當前環境
const getEnvironment = (): keyof typeof environments => {
  const env = import.meta.env.MODE as keyof typeof environments
  return environments[env] ? env : 'development'
}

// 在開發環境時，可選擇使用專門的dev配置
const shouldUseDevConfig = (): boolean => {
  return getEnvironment() === 'development' && import.meta.env.VITE_USE_DEV_CONFIG === 'true'
}

// 導出配置
export const config: AppConfig = (() => {
  if (shouldUseDevConfig()) {
    // 動態導入dev配置（需要在運行時處理）
    return environments.development // 暫時使用development配置
  }
  return environments[getEnvironment()]
})()

// 輔助函數
export const isDev = () => config.dev.debugMode
export const isProd = () => getEnvironment() === 'production'
export const isTest = () => getEnvironment() === 'test'

// 日誌工具
export const logger = {
  log: (...args: any[]) => {
    if (config.dev.enableLogging) {
      console.log('[LOTTERY]', ...args)
    }
  },
  error: (...args: any[]) => {
    if (config.dev.enableLogging) {
      console.error('[LOTTERY ERROR]', ...args)
    }
  },
  warn: (...args: any[]) => {
    if (config.dev.enableLogging) {
      console.warn('[LOTTERY WARN]', ...args)
    }
  },
  debug: (...args: any[]) => {
    if (config.dev.debugMode) {
      console.debug('[LOTTERY DEBUG]', ...args)
    }
  }
}

// API 端點配置
export const endpoints = {
  auth: {
    thirdPartyLogin: '/auth/thirdPartyLogin',
    refresh: '/auth/refresh',
    logout: '/auth/logout',
    login: '/auth/login',
    refreshToken: '/auth/refreshToken'
  },
  user: {
    profile: '/user/profile',
    balance: '/userAssets/get',
    updateBalance: '/user/balance'
  },
  game: {
    currentPeriod: '/periods/currentPeriod',
    placeBet: '/bets/place',
    betHistory: '/bet/history',
    gameResult: '/periods/latestDrawResult',
    gameRules:'/gameRule/list',
    betRecords:'/bets/records'
  },
  system: {
    config: '/system/config',
    notice: '/system/notice'
  }
} as const

export default config